import { useEffect, useRef, useState } from "react";
import { Share2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { MONTH_NAMES } from "@/lib/helpers";

const W = 1080, H = 1350;

async function loadPhoto(path) {
  if (!path) return null;
  try {
    const r = await http.get(`/files/${encodeURI(path)}`, { responseType: "blob" });
    const img = new Image();
    img.src = URL.createObjectURL(r.data);
    await img.decode();
    return img;
  } catch { return null; }
}

const fmtD = (d) => { const x = new Date(`${d}T00:00:00`); return `${x.getDate()} ${MONTH_NAMES[x.getMonth()]}`; };

export function champMeta(c) {
  const week = !!c.week_start, mgr = c.kind === "mgr";
  const title = `${week ? "HAFTANIN" : "AYIN"} ${mgr ? "MÜDÜRÜ" : c.current ? "LİDERİ" : "ŞAMPİYONU"}`;
  const period = week ? `${fmtD(c.week_start)} – ${fmtD(c.week_end)} ${c.week_end.slice(0, 4)}` : `${MONTH_NAMES[c.month - 1]} ${c.year}`;
  const key = week ? c.week_start : `${c.year}_${String(c.month).padStart(2, "0")}`;
  return { week, mgr, title, period, key,
    color: week ? ["#67e8f9", "#22d3ee", "#0891b2", "rgba(34,211,238,"] : ["#fde68a", "#f59e0b", "#ca8a04", "rgba(250,204,21,"],
    file: `${mgr ? "mudur" : "sampiyon"}_${week ? "hafta" : "ay"}_${key}.png` };
}

function rays(ctx, rgba) {
  ctx.save(); ctx.translate(W / 2, 590);
  for (let i = 0; i < 24; i++) {
    ctx.rotate((Math.PI * 2) / 24);
    ctx.fillStyle = i % 2 ? `${rgba}0.05)` : `${rgba}0.10)`;
    ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(-70, -900); ctx.lineTo(70, -900); ctx.closePath(); ctx.fill();
  }
  ctx.restore();
}

function crown(ctx, x, y, s, col) {
  ctx.save(); ctx.translate(x, y); ctx.fillStyle = col[1]; ctx.shadowColor = col[0]; ctx.shadowBlur = 30;
  ctx.beginPath(); ctx.moveTo(-s, s * 0.6); ctx.lineTo(-s, -s * 0.4); ctx.lineTo(-s * 0.5, s * 0.1); ctx.lineTo(0, -s * 0.7);
  ctx.lineTo(s * 0.5, s * 0.1); ctx.lineTo(s, -s * 0.4); ctx.lineTo(s, s * 0.6); ctx.closePath(); ctx.fill(); ctx.restore();
}

function avatar(ctx, img, name, col) {
  const cx = W / 2, cy = 590, r = 210;
  const g = ctx.createLinearGradient(cx - r, cy - r, cx + r, cy + r);
  g.addColorStop(0, col[0]); g.addColorStop(0.5, col[1]); g.addColorStop(1, col[2]);
  ctx.fillStyle = g; ctx.beginPath(); ctx.arc(cx, cy, r + 16, 0, Math.PI * 2); ctx.fill();
  ctx.save(); ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.clip();
  if (img) {
    const s = Math.max((2 * r) / img.width, (2 * r) / img.height);
    ctx.drawImage(img, cx - (img.width * s) / 2, cy - (img.height * s) / 2, img.width * s, img.height * s);
  } else {
    ctx.fillStyle = "#1e293b"; ctx.fillRect(cx - r, cy - r, 2 * r, 2 * r);
    ctx.fillStyle = "#e2e8f0"; ctx.font = "bold 150px sans-serif"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillText((name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase(), cx, cy + 8);
  }
  ctx.restore();
}

export async function renderChampionCard(c) {
  const mt = champMeta(c), col = mt.color;
  const cv = document.createElement("canvas");
  cv.width = W; cv.height = H;
  const ctx = cv.getContext("2d");
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, "#0b1220"); bg.addColorStop(1, "#020617");
  ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);
  rays(ctx, col[3]);
  ctx.strokeStyle = `${col[3]}0.55)`; ctx.lineWidth = 6; ctx.strokeRect(36, 36, W - 72, H - 72);
  ctx.textAlign = "center"; ctx.textBaseline = "alphabetic";
  ctx.fillStyle = "#94a3b8"; ctx.font = "600 34px sans-serif"; ctx.fillText("KARAHAN TEKNOLOJİ", W / 2, 120);
  ctx.fillStyle = col[0]; ctx.font = "800 64px sans-serif"; ctx.fillText(mt.title, W / 2, 205);
  ctx.fillStyle = "#cbd5e1"; ctx.font = "600 40px sans-serif"; ctx.fillText(mt.period, W / 2, 262);
  avatar(ctx, await loadPhoto(c.photo_path), c.name, col);
  crown(ctx, W / 2, 350, 70, col);
  ctx.fillStyle = "#ffffff"; ctx.font = "900 76px sans-serif"; ctx.fillText(c.name, W / 2, 900, W - 140);
  if (c.branch_name) { ctx.fillStyle = "#94a3b8"; ctx.font = "500 40px sans-serif"; ctx.fillText(c.branch_name, W / 2, 958, W - 140); }
  ctx.fillStyle = col[1]; ctx.font = "900 150px sans-serif"; ctx.fillText(Number(c.qty || 0).toLocaleString("tr-TR"), W / 2, 1110);
  ctx.fillStyle = col[0]; ctx.font = "700 40px sans-serif"; ctx.fillText(mt.mgr ? "ŞUBE TOPLAM ADET" : "ADET SATIŞ", W / 2, 1165);
  ctx.fillStyle = "#64748b"; ctx.font = "500 30px sans-serif"; ctx.fillText("Tebrikler! Emeğin ve başarın için teşekkürler.", W / 2, 1255);
  return new Promise((res) => cv.toBlob(res, "image/png"));
}

export function ShareChampionButton({ champ, className = "", label = "Tebrik Kartı", testId }) {
  const [busy, setBusy] = useState(false);
  const pre = useRef(null);
  useEffect(() => { pre.current = renderChampionCard(champ).catch(() => null); }, [champ]);
  const go = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      const blob = (await pre.current) || (await renderChampionCard(champ));
      const mt = champMeta(champ), fname = mt.file;
      const file = new File([blob], fname, { type: "image/png" });
      const text = `${mt.period} · ${mt.title.toLocaleLowerCase("tr-TR")}: ${champ.name}! Tebrikler 🏆`;
      if (navigator.canShare?.({ files: [file] })) {
        try { await navigator.share({ files: [file], title: mt.title, text }); return; }
        catch (err) { if (err?.name === "AbortError") return; }
      }
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = fname; a.click();
      toast.success("Tebrik kartı indirildi");
    } catch { toast.error("Kart oluşturulamadı"); } finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={go} disabled={busy} data-testid={testId || `share-champion-${champMeta(champ).mgr ? "mgr-" : ""}${champMeta(champ).key}`}
            className={`inline-flex items-center justify-center gap-1.5 rounded-full border border-yellow-400/50 bg-yellow-400/10 text-yellow-200 font-semibold hover:bg-yellow-400/20 transition-colors disabled:opacity-60 ${className}`}>
      {busy ? <Loader2 size={13} className="animate-spin" /> : <Share2 size={13} />} {label}
    </button>
  );
}
